import { Component } from 'react';

import isEqual from 'lodash/isEqual';

import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import FormField from '../FormField';

import GuestsRoomsChildrenPopover from './GuestsRoomsChildrenPopover';
import GuestsRoomsChildrenPortal from './GuestsRoomsChildrenPortal';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { Maybe } from '@shared/types/utils';

import STYLES from './GuestsRoomsChildrenSelect.module.scss';

import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';

type Props = {
  miniEventTracker: MiniEventTracker;
  adults: number;
  rooms: number;
  childrenAges: number[];
  getGuestsRoomsChildren: Function;
  label?: string;
  lightLabel?: boolean;
  collapsed?: boolean;
  masonry?: boolean;
  className?: Maybe<string>;
  stayEditClassName?: string;
  hideLabel?: boolean;
};

type State = {
  isOpen: boolean;
  rooms: number;
  adults: number;
  childrenAges: number[];
  adultChanged?: Maybe<boolean>;
  childrenAgesChanged?: Maybe<boolean>;
  roomNumChanged?: Maybe<boolean>;
};

const defaultProps = {
  lightLabel: false,
  collapsed: false,
  masonry: false,
};

const cls = cssModules(STYLES);

class GuestsRoomsChildrenSelect extends Component<Props, State> {
  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);

    this.state = {
      isOpen: false,
      rooms: props.rooms,
      adults: props.adults,
      childrenAges: props.childrenAges,
    };
  }

  onOpen = () => {
    window.name = 'DVSearchModalIsOpen';
    const { adults, childrenAges, rooms } = this.props;
    this.setState({
      isOpen: true,
      rooms,
      adults,
      childrenAges,
    });
    this.props.miniEventTracker.trackExperimentChokepoint(
      'hotel_search_controls_guests_rooms_children_select_rendered',
    );
  };

  onClose = (shouldUpdate: boolean = true) => {
    this.setState({
      isOpen: false,
    });
    if (shouldUpdate) {
      const {
        adultChanged,
        adults,
        childrenAges,
        childrenAgesChanged,
        roomNumChanged,
        rooms,
      } = this.state;
      this.props.getGuestsRoomsChildren({
        adults,
        adultChanged,
        childrenAges,
        childrenAgesChanged,
        rooms,
        roomNumChanged,
      });
    } else {
      const { adults, childrenAges, rooms } = this.props;
      this.setState({
        rooms,
        childrenAges,
        adults,
      });
    }
  };

  onGuestsRoomsChildrenChanged = (
    rooms: number,
    adults: number,
    childrenAges: number[],
  ) => {
    if (rooms !== this.state.rooms) {
      this.setState({
        rooms,
        roomNumChanged: true,
      });
    }

    if (adults !== this.state.adults) {
      this.setState({
        adults,
        adultChanged: true,
      });
    }

    if (!isEqual(childrenAges, this.state.childrenAges)) {
      this.setState({
        childrenAges,
        childrenAgesChanged: true,
      });
    }
  };

  render() {
    const {
      adults,
      childrenAges,
      className,
      collapsed,
      hideLabel,
      label,
      lightLabel,
      masonry,
      rooms,
      stayEditClassName,
    } = this.props;
    const {
      adults: adultsState,
      childrenAges: childrenAgesState,
      isOpen,
      rooms: roomState,
    } = this.state;
    const isApplyButtonDisabled =
      rooms === roomState &&
      adults === adultsState &&
      isEqual(childrenAges, childrenAgesState);
    const guestsRoomsFieldId = `guests-rooms${collapsed ? '-collapsed' : ''}${
      masonry ? '-masonry' : ''
    }`;

    return (
      <div className={cls('GuestsRoomsChildrenSelect', className)}>
        <FormField
          className={cls(
            hideLabel ? '' : 'GuestsRoomsChildrenSelect__formField',
            stayEditClassName,
          )}
          fieldId={guestsRoomsFieldId}
          lightLabel={lightLabel}
          label={label}
          hideLabel={hideLabel}
        >
          <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
            {(isMobile: boolean) =>
              isMobile ? (
                <GuestsRoomsChildrenPortal
                  adults={adults}
                  rooms={rooms}
                  childrenAges={childrenAges}
                  onlyShowGuestsNumber={hideLabel || !label}
                  className={
                    !hideLabel && label
                      ? cls('GuestsRoomsChildrenSelect__portal')
                      : null
                  }
                  isApplyButtonDisabled={isApplyButtonDisabled}
                  guestsRoomsFieldId={guestsRoomsFieldId}
                  onOpen={this.onOpen}
                  onClose={this.onClose}
                  onGuestsRoomsChildrenChanged={
                    this.onGuestsRoomsChildrenChanged
                  }
                />
              ) : (
                <GuestsRoomsChildrenPopover
                  isOpen={isOpen}
                  adults={adults}
                  rooms={rooms}
                  childrenAges={childrenAges}
                  guestsRoomsFieldId={guestsRoomsFieldId}
                  onOpen={this.onOpen}
                  onClose={this.onClose}
                  onGuestsRoomsChildrenChanged={
                    this.onGuestsRoomsChildrenChanged
                  }
                  label={label}
                  hideLabel={hideLabel}
                />
              )
            }
          </BpkBreakpoint>
        </FormField>
      </div>
    );
  }
}

export default withMiniEventTracker(GuestsRoomsChildrenSelect);
